import React from "react";
import Framework from "@beardeddevops/react.framework";
import Types from 'constants/Types';
import {TextField, Tooltip} from "@material-ui/core";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {Collections, Objects, Elements} from "components";
import BarcodeReader from 'react-barcode-reader';
import APPCONFIG from "../../constants/Config";
import API from "@beardeddevops/react.api";
import {toast} from "react-toastify";
import {confirmAlert} from "react-confirm-alert";
import syncBodyScrollLock from "../../utils/syncBodyScrollLock";
import {findServiceEntryIndex, existingCatalogServiceIds} from "../../utils/contractServiceEntries";
import {guardOpenWorkOrderBeforeAdd} from "../../utils/openWorkOrderWarning";
import {
	layawayWouldOverpayAfterChange,
} from "../../utils/layawayRefundDue";
import {computeLayawayContractTotals} from "../../utils/layawayContractTotals";
import LayawayOverpaymentNotice from "./LayawayOverpaymentNotice";
import {fflCatalogTaxable} from "../../utils/catalogTaxable";

function liNum(v) {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
}

function liBool(v) {
	if (v === true || v === 1 || v === '1' || v === 'true') return true;
	if (v === false || v === 0 || v === '0' || v === 'false') return false;
	return !!v;
}

/**
 * Client-side preview of merchandise + tax buckets (see layawayContractTotals.js).
 */
function computeLayawayDisplayTotals(entries, location, isFflLinked) {
	return computeLayawayContractTotals(entries, location, isFflLinked);
}

function layawayEntryApiType(entry) {
	if (entry?.type === 'service') {
		return Types.Layaways_Services._name;
	}
	if (entry?.type === 'product' && entry.product) {
		return Types.Layaways_Products._name;
	}
	return Types.Layaways_Items._name;
}

function layawayEntryPriceValue(entry) {
	return (
		entry?.price ??
		entry?.cost ??
		entry?.information?.skus?.price_retail ??
		entry?.information?.price ??
		entry?.information?.price_list
	);
}

class LayawayItems extends Framework.Components.Collection {
	constructor(props) {
		/**
		 *  @typedef {Layaways_Object} this.state
		 */
		super(props, Types.Layaways._name);
		this.state.items = [];
		this.state.products = [];
		this.state.sub_total_amount = 0.00;
		this.state.tax_amount = 0.00;
		this.state.amount = 0.00;
		this.state.deposit = 0.00;
		this.state.remaining = 0.00;
		this.state.itemIsChanging = false;
		this.state.layawayClosed = false;
		this.state.serverLayawayAmount = null;
		this.state.serverLayawayRemaining = null;
		this.state.overpaid_tax_total = 0;
		this.state.modalTaxOverpay = false;
		this.state.taxOverpayAction = "refund_credit";
		this.state.taxOverpayAmount = "";
		this.state.taxOverpayNote = "";

	}

	async componentDidMount() {
		if (!this.props.layaway) {
			super.componentDidMount();
		}
		if (this.props.layaway) {
			await this.fetchEntries();
		}
	}

	notifyContractChanged = (entries) => {
		if (this.props.layaway) {
			if (typeof this.props.onContractChanged === 'function') {
				this.props.onContractChanged();
			}
			return;
		}
		if (typeof this.props.updateEntries === 'function') {
			this.props.updateEntries(entries);
		}
	}

	async componentDidUpdate(prevProps) {
		if (this.props.layaway && this.props.layaway !== prevProps.layaway) {
			await this.fetchEntries();
		}
	}

	componentWillUnmount() {
		syncBodyScrollLock();
	}

	toggle = (forItem) => () => {
		const wasOpen = !!this.state[forItem];
		this.setState(
			{[forItem]: !wasOpen},
			wasOpen ? syncBodyScrollLock : undefined
		);
	};

	reportClose = (forItem) => () => {
		this.setState({[forItem]: false}, syncBodyScrollLock);
	};

	closeEditRowModal = () => {
		this.setState({itemIsChanging: false}, syncBodyScrollLock);
	};

	closeTaxOverpayModal = () => {
		this.setState({modalTaxOverpay: false}, syncBodyScrollLock);
	};

	fetchEntries = async () => {
		if (!this.props.layaway) {
			return null;
		}
		let model = new API.BuildClass(Types.Layaways_Entries._name, API.RequestType.READ);
		model.parent = this.props.layaway;
		model = await model.submit(null, 'GET');
		const entries = model.items || [];
		let layawayClosed = false;
		let serverLayawayAmount = null;
		let serverLayawayRemaining = null;
		let overpaid_tax_total = 0;
		try {
			let lw = new API.BuildClass(Types.Layaways._name, API.RequestType.READ);
			lw.object = {pkey: this.props.layaway};
			lw = await lw.submit(null, 'GET', true);
			const o = lw?.response ?? lw?.object ?? lw;
			const cd = o?.completed_date ?? lw?.completed_date;
			layawayClosed = cd != null && String(cd).trim() !== '';
			serverLayawayAmount = o?.amount ?? lw?.amount;
			serverLayawayRemaining = o?.remaining ?? lw?.remaining;
			overpaid_tax_total = liNum(o?.overpaid_tax_total ?? o?.OverpaidTaxTotal ?? 0);
		} catch (e) {
			// keep defaults; line edits still blocked server-side when closed
		}
		this.setState({
			entries,
			layawayClosed,
			serverLayawayAmount,
			serverLayawayRemaining,
			overpaid_tax_total,
		});
		return entries;
	}

	handleItemUpdate = (event) => {
		this.state.itemToChange[event.target.id] = event.target.value;
		this.setState({itemToChange: this.state.itemToChange});
	}

	submitTaxOverpay = async () => {
		if (!this.props.layaway || this.state.layawayClosed) {
			return;
		}
		const amt =
			this.state.taxOverpayAmount !== "" && this.state.taxOverpayAmount != null
				? liNum(this.state.taxOverpayAmount)
				: liNum(this.state.overpaid_tax_total);
		const m = new API.BuildClass(Types.Layaways._name, API.RequestType.UPDATE);
		m.object = {
			pkey: this.props.layaway,
			tax_overpayment_action: this.state.taxOverpayAction,
			tax_overpayment_amount: amt,
			tax_overpayment_note: this.state.taxOverpayNote || "",
		};
		try {
			await m.submit(null, "POST", true);
			API.Toast.success("Recorded.", { position: API.Toast.POSITION.TOP_RIGHT, autoClose: 5000 });
			this.setState({ modalTaxOverpay: false }, syncBodyScrollLock);
			await this.fetchEntries();
		} catch (e) {
			API.Toast.error("Could not update layaway.", { position: API.Toast.POSITION.TOP_RIGHT });
		}
	}

	doSaveRowAfterConfirm = async () => {
		let entries = this.state.entries;
		let index = entries.findIndex((entry) => entry.pkey === this.state.itemToChange.pkey)
		entries[index] = this.state.itemToChange;
		if (this.props.layaway) {
			const rowModel = new API.BuildClass(layawayEntryApiType(this.state.itemToChange), API.RequestType.UPDATE);
			rowModel.object = this.state.itemToChange

			try {
				await rowModel.submit(null, 'POST', true);
				API.Toast.success("Saved!", {
					position: API.Toast.POSITION.TOP_RIGHT,
					autoClose: 8000
				})
				const synced = await this.fetchEntries();
				this.notifyContractChanged(synced);
				this.setState({itemToChange: null, itemIsChanging: false}, syncBodyScrollLock);
			} catch (e) {
				API.Toast.error(e?.message || 'Could not save line.', { position: API.Toast.POSITION.TOP_RIGHT });
			}
			return;
		}
		let total = 0;
		entries.forEach((item) => {
			const unit = item.price != null && item.price !== ''
				? parseFloat(item.price)
				: item.cost != null && item.cost !== ''
					? parseFloat(item.cost)
					: item.information?.skus?.price_retail != null
						? parseFloat(item.information.skus.price_retail)
						: parseFloat(item.information?.price_list ?? 0);
			if (item.information?.quantity || item.quantity) {
				total += parseFloat(item.quantity ? item.quantity : item.information.quantity) * unit;
			} else {
				total += unit;
			}
		})
		this.notifyContractChanged(entries)
		this.setState({entries, itemToChange: null, itemIsChanging: false}, syncBodyScrollLock)
	}

	handleSavingRowModal = async () => {
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed and cannot be edited.');
			return;
		}
		const {location} = this.props.rootState;
		const isFflLinked = !!this.props.rootState.ffl_boss_linked;
		const entries = [...(this.state.entries || [])];
		const index = entries.findIndex((entry) => entry.pkey === this.state.itemToChange.pkey);
		if (index < 0) {
			return;
		}
		entries[index] = this.state.itemToChange;
		const preview = computeLayawayDisplayTotals(entries, location, isFflLinked);
		const amt = this.state.serverLayawayAmount != null ? liNum(this.state.serverLayawayAmount) : liNum(this.state.amount);
		const rem = this.state.serverLayawayRemaining != null ? liNum(this.state.serverLayawayRemaining) : liNum(this.state.remaining);
		const wouldRefund = layawayWouldOverpayAfterChange(preview.amount, amt, rem);

		const run = () => this.doSaveRowAfterConfirm();

		if (this.props.layaway && wouldRefund) {
			confirmAlert({
				title: 'Confirm contract change',
				message: (
					<span>
						<LayawayOverpaymentNotice
							contractAmount={amt}
							contractRemaining={rem}
							previewContractAmount={preview.amount}
							actionLabel="This edit"
						/>
						<br/><br/>
						Add any needed lines first if they should keep a balanced open contract.
					</span>
				),
				buttons: [
					{label: 'Go back', onClick: () => syncBodyScrollLock()},
					{label: 'Save anyway', onClick: () => { run().then(() => syncBodyScrollLock()); }},
				],
			});
			return;
		}
		await run();
	}

	addItems = async (items) => {
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed; items cannot be added.');
			return;
		}
		for (const item of items) {
			const label = item?.skus?.name || item?.skus?.sku || 'This item';
			const proceed = await guardOpenWorkOrderBeforeAdd(item?.pkey, label);
			if (!proceed) {
				return;
			}
		}
		//in here need to do an update if it exists or a create if it doesnt but only if layaway is passed in FML
		let total = parseFloat(this.state.sub_total_amount);
		let entries = this.state.entries ?? [];
		items.forEach(item => {
			let found = entries.find((entry) => entry.pkey === item.pkey || entry.pkey === item.sku || entry.inventory === item.sku || entry.inventory === item.pkey)
			if (!found) {
				let defTax = true;
				if (item.taxable != null) {
					defTax = liBool(item.taxable);
				} else if (item.skus?.taxable != null) {
					defTax = liBool(item.skus.taxable);
				}
				entries.push({pkey: item.pkey, information: item, type: 'item', taxable: defTax})
			} else {
				let index = entries.findIndex((entry) => entry.pkey === item.pkey || entry.pkey === item.sku || entry.inventory === item.sku || entry.inventory === item.pkey)
				// console.log(item, entries[index])
				entries[index].quantity ? entries[index].quantity += item.quantity : entries[index].information.quantity += item.quantity
			}
			total += parseFloat(item.skus.price_retail * item.quantity)
		})
		if (this.props.layaway) {
			this.setState({loading: true});
			let item = new API.BuildClass(Types.Layaways_Items._name, API.RequestType.CREATE);
			item.object.entries = entries;
			item.parent = this.props.layaway
			try {
				await item.submit(null, API.RequestType.CREATE);
				const synced = await this.fetchEntries();
				this.notifyContractChanged(synced);
			} catch (error) {
				API.Toast.error(error?.message || 'Could not add items to layaway.', { position: API.Toast.POSITION.TOP_RIGHT });
			} finally {
				this.setState({loading: false});
			}
		} else {
			this.setState({entries, sub_total_amount: total});
			this.notifyContractChanged(entries);
		}
	}

	addServices = async (services) => {
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed; services cannot be added.');
			return;
		}
		let total = parseFloat(this.state.sub_total_amount);
		let entries = [...(this.state.entries ?? [])];
		const skipped = [];
		const toAdd = [];
		services.forEach((service) => {
			const serviceKey = service.pkey;
			if (findServiceEntryIndex(entries, serviceKey) >= 0) {
				skipped.push(service.name || 'Service');
				return;
			}
			toAdd.push(service);
		});
		if (skipped.length > 0) {
			toast.info(
				`${skipped.join(', ')} already on this layaway. Edit the line to change quantity.`,
				{ position: toast.POSITION.TOP_RIGHT }
			);
		}
		if (toAdd.length === 0) {
			return;
		}
		if (this.props.layaway) {
			this.setState({loading: true});
			try {
				for (const service of toAdd) {
					const qty = service.quantity != null ? parseInt(service.quantity, 10) : 1;
					const defTax = service.taxable != null ? liBool(service.taxable) : true;
					const row = new API.BuildClass(Types.Layaways_Services._name, API.RequestType.CREATE);
					row.parent = this.props.layaway;
					row.object = {
						service: service.pkey,
						quantity: String(qty),
						taxable: defTax,
						price: service.price,
						name: service.name,
					};
					await row.submit(null, API.RequestType.CREATE);
				}
				const synced = await this.fetchEntries();
				this.notifyContractChanged(synced);
			} catch (error) {
				API.Toast.error(error?.message || 'Could not add services to layaway.', { position: API.Toast.POSITION.TOP_RIGHT });
			} finally {
				this.setState({loading: false});
			}
		} else {
			toAdd.forEach((service) => {
				const qty = service.quantity != null ? parseInt(service.quantity, 10) : 1;
				const linePrice = parseFloat(service.price || 0);
				const defTax = service.taxable != null ? liBool(service.taxable) : true;
				entries.push({
					pkey: service.pkey,
					service: service.pkey,
					information: service,
					type: 'service',
					taxable: defTax,
					quantity: qty,
				});
				total += linePrice * qty;
			});
			this.setState({entries, sub_total_amount: total});
			this.notifyContractChanged(entries);
		}
	};

	addProducts = async (products) => {
		if (!this.props.rootState.ffl_boss_linked) {
			toast.error('FFL Boss is not linked to this account.');
			return;
		}
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed; products cannot be added.');
			return;
		}
		let total = parseFloat(this.state.sub_total_amount);
		let entries = this.state.entries ?? [];
		entries = [...entries, ...products.map(
			product => {
				total += parseFloat(product.price_list);
				const defTax = fflCatalogTaxable(
					product.taxable,
					!!this.props.rootState.ffl_force_taxable,
					true
				);
				return {
					pkey: product.pkey,
					information: product,
					type: 'product',
					taxable: defTax,
				}
			}
		)];
		if (this.props.layaway) {
			this.setState({loading: true});
			let item = new API.BuildClass(Types.Layaways_Items._name, API.RequestType.CREATE);
			item.object.entries = entries;
			item.parent = this.props.layaway
			try {
				await item.submit(null, API.RequestType.CREATE);
				const synced = await this.fetchEntries();
				this.notifyContractChanged(synced);
			} catch (error) {
				API.Toast.error(error?.message || 'Could not add products to layaway.', { position: API.Toast.POSITION.TOP_RIGHT });
			} finally {
				this.setState({loading: false});
			}
		} else {
			this.setState({entries, sub_total_amount: total});
			this.notifyContractChanged(entries);
		}
	}

	deleteItem = async (index) => {
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed; lines cannot be removed.');
			return;
		}
		let entries = JSON.parse(JSON.stringify(this.state.entries || []));
		const entry = entries[index];
		if (!entry) {
			return;
		}
		if (this.props.layaway) {
			const {location} = this.props.rootState;
			const isFflLinked = !!this.props.rootState.ffl_boss_linked;
			const after = entries.filter((_, i) => i !== index);
			const preview = computeLayawayDisplayTotals(after, location, isFflLinked);
			const amt = this.state.serverLayawayAmount != null ? liNum(this.state.serverLayawayAmount) : liNum(this.state.amount);
			const rem = this.state.serverLayawayRemaining != null ? liNum(this.state.serverLayawayRemaining) : liNum(this.state.remaining);
			const wouldRefund = layawayWouldOverpayAfterChange(preview.amount, amt, rem);
			const lastLine = entries.length === 1;

			confirmAlert({
				title: 'Confirm Delete Line Item',
				message: (
					<span>
						Remove this line from the layaway contract?
						{lastLine ? (
							<>
								<br/><br/>
								This removes the last merchandise line. The <strong>layaway record is kept</strong> for audit
								and payments remain visible; it is not deleted.
							</>
						) : null}
						{wouldRefund ? (
							<>
								<br/><br/>
								<LayawayOverpaymentNotice
									contractAmount={amt}
									contractRemaining={rem}
									previewContractAmount={preview.amount}
									actionLabel="Removing this line"
								/>
							</>
						) : null}
					</span>
				),
				buttons: [
					{label: 'Cancel', onClick: () => syncBodyScrollLock()},
					{
						label: 'Delete Line Item',
						onClick: async () => {
							try {
								this.setState({loading: true});
								let deleteType = layawayEntryApiType(entry);
								let item = new API.BuildClass(deleteType, API.RequestType.DELETE);
								item.object.pkey = entry.pkey;
								item.object.layaway = this.props.layaway;
								await item.submit(null, API.RequestType.DELETE);
								const synced = await this.fetchEntries();
								this.notifyContractChanged(synced);
							} catch (error) {
								API.Toast.error(error?.message || 'Could not remove line from layaway.', { position: API.Toast.POSITION.TOP_RIGHT });
							} finally {
								this.setState({loading: false});
							}
						},
					},
				],
			});
		} else {
			entries.splice(index, 1);
			this.setState({entries});
			this.notifyContractChanged(entries);
		}

	}

	handleToggleItemModel = (index) => {
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed and cannot be edited.');
			return;
		}
		const item = this.state.entries[index];
		if (!item) {
			alert("There was an issue editing this item. Please contact support.")
			return;
		}

		//the below JSOn nonsense is to make a shallow copy so it doesn't change the state
		this.setState({itemToChange: JSON.parse(JSON.stringify(item)), itemIsChanging: true})
	}

	handleTaxableChange = async (index, value) => {
		if (this.state.layawayClosed) {
			toast.info('This layaway is completed and cannot be edited.');
			return;
		}
		let entries = this.state.entries;
		let item = entries[index];
		if (!item) {
			alert("There was an issue editing this item. Please contact support.")
			return;
		}
		item = JSON.parse(JSON.stringify(item));
		item.taxable = value
		entries[index] = item;
		if (this.props.layaway) {
			const rowModel = new API.BuildClass(layawayEntryApiType(item), API.RequestType.UPDATE);
			rowModel.object = item

			try {
				await rowModel.submit(null, 'POST', true);
				const synced = await this.fetchEntries();
				this.notifyContractChanged(synced);
			} catch (e) {
				API.Toast.error(e?.message || 'Could not update taxable status.', { position: API.Toast.POSITION.TOP_RIGHT });
			}
			return;
		}
		this.setState({entries})
		this.notifyContractChanged(entries)
	}

	handleReportModalClose = (forItem) => () => {
		this.setState({[forItem]: false}, syncBodyScrollLock);
	}

	handleAddPayment = (payment) => {
		this.state.cash_amount = payment.cash_amount;
		this.state.check_amount = payment.check_amount;
		this.state.zelle_amount = payment.zelle_amount;
		let deposit = (parseFloat(payment.cash_amount) || 0) + (parseFloat(payment.check_amount) || 0) + (parseFloat(payment.zelle_amount) || 0);
		this.setState({deposit: parseFloat(deposit)});
	}

	handleScan = (data) => {
		this.fetchScanData(data).then((r) => r);
	}

	handleError = (err) => {
		console.log('Scan Error', err);
	}

	fetchScanData = async (data) => {
		let productType = data.split('-')[0];
		data = data.split('-')[1];
		// If scanned item is an Item
		if (productType === 'I') {
			$.ajax({
				type: "GET",
				url: APPCONFIG.settings.apiURL() + '/' + APPCONFIG.settings.apiVersion + '/inventories/' + data,
				success: (data) => {
					API.ToastId = toast.info(data.response.skus.name + ' added to layaway.', {
						position: toast.POSITION.TOP_RIGHT
					});
					data.response.quantity = 1;
					this.addItems([data.response]).then((r) => r);
				},
				error: function (xhr, status, error) {
					//TODO: change this error msg
					let errorMessage = xhr.responseJSON.response === 'Product is already in cart' ? xhr.responseJSON.response : 'We were unable to find that product you requested on your account.'
					API.ToastId = toast.error(errorMessage, {
						position: toast.POSITION.TOP_CENTER
					});
				}
			});
			// If scanned item is a Product (FFL Boss)
		} else {
			if (!this.props.rootState.ffl_boss_linked) {
				toast.error('FFL Boss is not linked to this account.');
				return;
			}
			$.ajax({
				type: "GET",
				url: APPCONFIG.settings.apiURL() + '/' + APPCONFIG.settings.apiVersion + '/inventories/connected?search==pkey:' + data + ";",
				success: (data) => {
					API.ToastId = toast.info(data.response.items[0].manufacturers.name + ' ' + data.response.items[0].model + ' added to layaway.', {
						position: toast.POSITION.TOP_RIGHT
					});
					data.response.quantity = 1;
					this.addProducts([data.response.items[0]]).then((r) => r);
				},
				error: function (xhr, status, error) {
					//TODO: change this error msg
					let errorMessage = xhr.responseJSON.response === 'Product is already in cart' ? xhr.responseJSON.response : 'We were unable to find that product you requested on your account.'
					API.ToastId = toast.error(errorMessage, {
						position: toast.POSITION.TOP_CENTER
					});
				}
			});
		}
	}

	render = () => {
		/**
		 * @type {Layaways_Properties} properties
		 */
		let properties = this.model.properties;
		/**
		 *  @type {Layaways_Object} object
		 */
		let {location} = this.props.rootState;
		const isFflLinked = !!this.props.rootState.ffl_boss_linked;
		const closed = !!this.state.layawayClosed;
		const totals = computeLayawayDisplayTotals(this.state.entries || [], location, isFflLinked);
		Object.assign(this.state, {
			sub_total_amount: totals.sub_total_amount,
			tax_amount: totals.tax_amount,
			amount: totals.amount,
			tax_rate: totals.header_display_tax_rate,
			tax_summary: totals.tax_summary,
			tax_json: totals.tax_json,
			remaining: (totals.amount - liNum(this.state.deposit)).toFixed(2),
		});
		let object = this.state;
		const displayName = this.state.itemToChange
			? this.state.itemToChange.name
				? this.state.itemToChange.name
				: this.state.itemToChange.type === 'product'
					? ((this.state.itemToChange.information.manufacturers ? this.state.itemToChange.information.manufacturers?.name : this.state.itemToChange.information.manufacturer_other) + ' | ' + this.state.itemToChange.information.model + ' | ' + (this.state.itemToChange.information.calibers ? this.state.itemToChange.information.calibers?.name : this.state.itemToChange.information.caliber_other))
					: this.state.itemToChange.type === 'service'
						? (this.state.itemToChange.information?.name || this.state.itemToChange.information?.skus?.name)
						: this.state.itemToChange.information.skus?.name
			: null;

		return (
			<>
				{!closed && this.props.layaway && liNum(this.state.overpaid_tax_total) > 0.005 && (
					<div className="alert alert-warning" style={{ marginBottom: "10px" }}>
						<strong>Tax overpayment</strong> — prior payments allocated about{" "}
						${liNum(this.state.overpaid_tax_total).toFixed(2)} more tax than the current contract buckets
						require.{" "}
						<Button color="link" size="sm" style={{ padding: 0, verticalAlign: "baseline" }} type="button" onClick={() =>
							this.setState({
								modalTaxOverpay: true,
								taxOverpayAmount: String(liNum(this.state.overpaid_tax_total).toFixed(2)),
							})
						}>
							Resolve…
						</Button>
					</div>
				)}
				<fieldset>
					<div className={'row'}>
						<div className={'col-lg-12'}>
							<div className={'section-title'}>
								<h4 className="section-title">Layaway Items</h4>
								<p style={{textAlign: 'center', color: 'white', marginBottom: '0px', marginTop: '5px'}}>
									<i>{object.entries?.length ?? 0} Total Items</i>
								</p>
								<div className={"button-container"}>
									<Tooltip title={closed ? 'Layaway is completed' : 'Add Best POS Products '}>
										<span>
											<Button className={'primary-button'} size={'sm'}
											        disabled={closed}
											        style={{ marginTop: '3px', marginRight: '2px' }}
											        onClick={closed ? undefined : this.toggle('modalLayawayItems')}>
												<i className={"fa fa-plus"}/>
												<span className="hidden-xs">&nbsp;Add Item</span>
											</Button>
										</span>
									</Tooltip>
									{isFflLinked && (
										<Tooltip style={{marginLeft: '0px'}} title={closed ? 'Layaway is completed' : 'Add FFL Boss Products '}>
											<span>
											<Button className={'primary-button'} size={'sm'}
											        disabled={closed}
											        style={{ marginTop: '3px', marginRight: '2px' }}
											        onClick={closed ? undefined : this.toggle('modalLayawayProducts')}>
												<i className={"fa fa-plus"}/>
												<span className="hidden-xs">&nbsp;Add Product</span>
											</Button>
											</span>
										</Tooltip>
									)}
									<Tooltip style={{marginLeft: '0px'}} title={closed ? 'Layaway is completed' : 'Add billable services'}>
										<span>
											<Button className={'primary-button'} size={'sm'}
											        disabled={closed}
											        style={{ marginTop: '3px', marginRight: '2px' }}
											        onClick={closed ? undefined : this.toggle('modalLayawayServices')}>
												<i className={"fa fa-plus"}/>
												<span className="hidden-xs">&nbsp;Add Service</span>
											</Button>
										</span>
									</Tooltip>
								</div>
								<BarcodeReader
									onError={this.handleError}
									onScan={closed ? () => {} : this.handleScan}
								/>
							</div>
							{this.state.entries?.length > 0 &&
								<div style={{
									overflowY: "auto",
									paddingRight: "4px"
								}}>

									{this.state.entries?.map((entry, i) => {
										if (entry.type === 'service') {
											const svcInfo = {
												skus: {
													name: entry.name || entry.information?.name,
													price_retail: entry.price ?? entry.information?.price,
													sku: entry.sku || 'SVC',
													serial_number: '',
													taxable: entry.taxable ?? entry.information?.taxable,
												},
												quantity: entry.quantity ?? 1,
											};
											return (
												<Elements.Item
													key={i}
													entry={{...entry, information: svcInfo}}
													product={svcInfo}
													onDelete={closed ? undefined : async () => {
														this.deleteItem(i).then();
													}}
													openModal={closed ? () => {
														toast.info('This layaway is completed and cannot be edited.');
													} : () => {
														this.handleToggleItemModel(i);
													}}
													handleTaxableChange={closed ? async () => {
														toast.info('This layaway is completed and cannot be edited.');
													} : async (value) => {
														this.handleTaxableChange(i, value).then();
													}}
													modalUpdate={this.state['itemUpdate' + i]}
													history={this.props.history}
												/>
											);
										}
										return entry.type === 'product' ? <Elements.Product
											key={i}
											entry={entry}
											product={entry.information}
											onDelete={closed ? undefined : async () => {
												this.deleteItem(i).then()
											}}
											openModal={closed ? () => {
												toast.info('This layaway is completed and cannot be edited.');
											} : () => {
												this.handleToggleItemModel(i)
											}}
											handleTaxableChange={closed ? async () => {
												toast.info('This layaway is completed and cannot be edited.');
											} : async (value) => {
												this.handleTaxableChange(i, value).then()
											}}
											modalUpdate={this.state['itemUpdate' + i]}
											history={this.props.history}
										/> : <Elements.Item
											key={i}
											entry={entry}
											product={entry.information}
											onDelete={closed ? undefined : async () => {
												this.deleteItem(i).then()
											}}
											openModal={closed ? () => {
												toast.info('This layaway is completed and cannot be edited.');
											} : () => {
												this.handleToggleItemModel(i)
											}}
											handleTaxableChange={closed ? async () => {
												toast.info('This layaway is completed and cannot be edited.');
											} : async (value) => {
												this.handleTaxableChange(i, value).then()
											}}
											modalUpdate={this.state['itemUpdate' + i]}
											history={this.props.history}
										/>
									})}
									<p style={{textAlign: 'center', color: 'grey'}}>
										<i>{object.entries?.length ?? 0} Total Items</i></p>
								</div>

							}
						</div>
					</div>
				</fieldset>

				{this.state.itemToChange && <Modal
					isOpen={this.state.itemIsChanging}
					toggle={this.closeEditRowModal}
					size={'md'}
					backdrop={true}
					style={{opacity: 1}}
				>
					<ModalHeader toggle={this.closeEditRowModal}>
						Update
						for: {displayName}
					</ModalHeader>
					<ModalBody>
						<div className={"row"}>
							<div className="col-12">
								<div className={"form-group"} style={{display: 'grid'}}>
									<TextField
										value={displayName}
										id="name"
										label="Name"
										variant="outlined"
										onChange={this.handleItemUpdate}
									/>
								</div>
							</div>
							<div className="col-12">
								<div className={"form-group"} style={{display: 'grid'}}>
									<TextField
										value={this.state.itemToChange.serial_number ?? this.state.itemToChange.information.skus?.serial_number ?? this.state.itemToChange.information.serial_number}
										id="serial_number"
										label="Serial Number"
										variant="outlined"
										onChange={this.handleItemUpdate}
									/>
								</div>
							</div>
							<div className="col-12">
								<div className={"form-group"} style={{display: 'grid'}}>
									<TextField
										value={this.state.itemToChange.sku ?? this.state.itemToChange.information.skus?.sku}
										id="sku"
										label="Sku"
										variant="outlined"
										onChange={this.handleItemUpdate}
									/>
								</div>
							</div>
							<div className="col-12">
								<div className={"form-group"} style={{display: 'grid'}}>
									<TextField
										value={layawayEntryPriceValue(this.state.itemToChange)}
										id="price"
										label="Price"
										variant="outlined"
										onChange={this.handleItemUpdate}
									/>
								</div>
							</div>
							<div className="col-12">
								<div className={"form-group"} style={{display: 'grid'}}>
									<TextField
										value={this.state.itemToChange.discount_amount}
										id="discount_amount"
										label="Discount Amount"
										variant="outlined"
										onChange={this.handleItemUpdate}
									/>
								</div>
							</div>
							{this.state.itemToChange.type !== 'product' && <div className="col-12">
								<div className={"form-group"} style={{display: 'grid'}}>
									<TextField
										value={this.state.itemToChange.quantity ?? this.state.itemToChange.information.quantity ?? 1}
										id="quantity"
										label="Quantity"
										variant="outlined"
										onChange={this.handleItemUpdate}
									/>
								</div>
							</div>}
						</div>
					</ModalBody>
					<ModalFooter style={{justifyContent: "space-between"}}>
						<Button type={"button"} className="btn secondary-button"
						        onClick={this.closeEditRowModal}>Close</Button>
						<Button type={"button"} className="btn primary-button"
						        disabled={closed}
						        onClick={() => this.handleSavingRowModal()}>Save</Button>
					</ModalFooter>
				</Modal>}

				<Framework.Elements.UniversalModal
					// isDev={APPCONFIG.settings.isDev()}
					locations={this.props.locations}
					name={"Layaway Items"}
					object={Collections.LayawayItems}
					history={this.props.history}
					toggle={this.state['modalLayawayItems']}
					reportClose={this.reportClose('modalLayawayItems')}
					afterSave={this.addItems}
					size={'xl'}
					objectProps={{inStock: true}}
					project={'pos'}
				/>
				{isFflLinked && (
				<Framework.Elements.UniversalModal
					// isDev={APPCONFIG.settings.isDev()}
					locations={this.props.locations}
					name={"Layaway Products"}
					object={Collections.LayawayProducts}
					history={this.props.history}
					toggle={this.state['modalLayawayProducts']}
					reportClose={this.reportClose('modalLayawayProducts')}
					afterSave={this.addProducts}
					size={'xl'}
					project={'pos'}
				/>
				)}
				<Framework.Elements.UniversalModal
					locations={this.props.locations}
					name={"Layaway Services"}
					object={Collections.LayawayServices}
					history={this.props.history}
					toggle={this.state['modalLayawayServices']}
					reportClose={this.reportClose('modalLayawayServices')}
					afterSave={this.addServices}
					size={'xl'}
					objectProps={{
						existingServiceIds: existingCatalogServiceIds(this.state.entries),
					}}
					project={'pos'}
				/>
				<Modal isOpen={this.state.modalTaxOverpay} toggle={this.closeTaxOverpayModal}>
					<ModalHeader toggle={this.closeTaxOverpayModal}>Resolve tax overpayment</ModalHeader>
					<ModalBody>
						<p style={{ marginBottom: "12px" }}>
							Choose how to document this situation. <strong>Credit layaway balance</strong> posts a negative
							adjustment payment (increases remaining balance) for the amount you specify, up to the
							over-applied tax shown on the layaway.
						</p>
						<div className="form-group">
							<TextField
								select
								fullWidth
								label="Action"
								variant="outlined"
								SelectProps={{ native: true }}
								value={this.state.taxOverpayAction}
								onChange={(e) => this.setState({ taxOverpayAction: e.target.value })}
							>
								<option value="refund_credit">Credit layaway balance (adjustment payment)</option>
								<option value="reapply_note">Append bookkeeping note to layaway notes</option>
								<option value="dismiss">Dismiss (audit entry only)</option>
							</TextField>
						</div>
						<div className="form-group">
							<TextField
								fullWidth
								label="Amount (defaults to full overpayment)"
								variant="outlined"
								value={this.state.taxOverpayAmount}
								onChange={(e) => this.setState({ taxOverpayAmount: e.target.value })}
							/>
						</div>
						<div className="form-group">
							<TextField
								fullWidth
								multiline
								rows={2}
								label="Note (optional)"
								variant="outlined"
								value={this.state.taxOverpayNote}
								onChange={(e) => this.setState({ taxOverpayNote: e.target.value })}
							/>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button className="secondary-button" onClick={this.closeTaxOverpayModal}>
							Cancel
						</Button>
						<Button className="primary-button" onClick={() => this.submitTaxOverpay()}>
							Submit
						</Button>
					</ModalFooter>
				</Modal>
				<Framework.Elements.UniversalModal
					// isDev={APPCONFIG.settings.isDev()}
					locations={this.props.locations}
					name={"Layaway Payment"}
					object={Objects.AddLayawayPayment}
					history={this.props.history}
					toggle={this.state['modalPayments']}
					reportClose={this.reportClose('modalPayments')}
					//afterSave={this.addProducts}
					size={'lg'}
					objectProps={{
						handleAddPayment: this.handleAddPayment,
						total: this.state.amount,
					}}
				/>
			</>
		)
	}
}

export default Framework.Components.withRootState(LayawayItems);
